/**
 * Single source of truth for app install promotion: store URLs, mobile-browser
 * detection, install-link helper. Every install-CTA surface (banner, email
 * badges, /get-app route, settings tile) reads from here.
 *
 * Values are public, immutable, and baked into the shipped apps — there is
 * no env-var indirection. Universal Links / store URLs / package names
 * don't differ between environments and don't rotate. To kill the feature
 * in an incident, revert the code or roll back the deploy.
 *
 * Note: `isNativePlatform()` in lib/utils/platform.ts answers "are we in the
 * Capacitor WebView." `detectMobileBrowser()` here answers the complementary
 * question: "is this a real mobile browser where we should pitch the app."
 */

export const IOS_APP_STORE_ID = '6760566881'
export const IOS_APP_STORE_URL = 'https://apps.apple.com/us/app/alumnia-alumni-network/id6760566881'
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=net.alumnia.app'
export const ANDROID_PACKAGE = 'net.alumnia.app'

/**
 * Cookie name + TTL used by the mobile-web install banner's "dismiss" action.
 * Centralized here so the server action (write) and the server-component
 * wrapper (read) can't drift. Don't hardcode either value at a call site.
 */
export const INSTALL_BANNER_DISMISS_COOKIE = 'alumnia_install_banner_dismissed'
// 14d. First close is soft, not punitive — a user who fat-fingers the X
// recovers in two weeks. Top apps use exponential backoff; this is the
// first step. Re-evaluate after seeing PostHog dismiss-rate by week.
export const INSTALL_BANNER_DISMISS_TTL_DAYS = 14
export const INSTALL_BANNER_DISMISS_TTL_MS = INSTALL_BANNER_DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000
export const INSTALL_BANNER_DISMISS_TTL_SECONDS = INSTALL_BANNER_DISMISS_TTL_DAYS * 24 * 60 * 60

/**
 * Per-browser-session impression cookie. Written by the client island on the
 * first IntersectionObserver impression and read by the server wrapper to
 * suppress the banner on every subsequent route within the same session.
 * No maxAge → browser-session cookie (clears on browser close). This is the
 * "shown once per session" gate that prevents the banner from re-rendering
 * on every nested page-view (event detail, profile, DM, etc.).
 */
export const INSTALL_BANNER_SESSION_COOKIE = 'alumnia_install_banner_seen'

/**
 * Cross-device suppression window after a successful native-app heartbeat.
 * Kept at 30 days regardless of dismiss-cookie TTL: heartbeat is positive
 * evidence the user has the app, dismiss is a negative signal — they
 * shouldn't share a TTL.
 */
export const NATIVE_SEEN_SUPPRESS_MS = 30 * 24 * 60 * 60 * 1000

/**
 * Locale → install-badge filename suffix. Mirrors `lib/i18n/config.ts:locales`
 * — falls back to 'en' for any unknown value. Shared by the email install
 * block, the landing-page footer, the settings dialog, and `/get-app`.
 */
export function getBadgeLocaleSuffix(locale: string | undefined | null): 'en' | 'es' | 'fr' {
  if (locale === 'es') return 'es'
  if (locale === 'fr') return 'fr'
  return 'en'
}

export type MobileBrowserPlatform = 'ios' | 'android'

/**
 * Bots, link unfurlers, and — critically — enterprise email security scanners
 * (Mimecast, Proofpoint, Barracuda, Microsoft SafeLinks, etc.) that follow
 * every link in a message to scan it. These must NOT count as install clicks:
 * the `/get-app` redirect logs a server-side event, and an un-filtered scanner
 * hit would inflate the email funnel with phantom clicks.
 *
 * Note: this deliberately does NOT include in-app browsers (Instagram, FB) —
 * those are real humans; they're excluded from the banner separately because
 * embedded browsers handle store URLs poorly, but their install clicks count.
 */
const BOT_UA_RE =
  /bot|crawler|spider|preview|facebookexternalhit|slackbot|twitterbot|whatsapp|telegrambot|discordbot|linkedinbot|embedly|mimecast|proofpoint|barracuda|messagelabs|safelinks|symantec|cloudmark|forcepoint|trendmicro|googleimageproxy|bingpreview|go-http-client|python-requests|curl\/|wget|axios|node-fetch|headless/i

/**
 * True when the UA is a bot / link-scanner / programmatic fetcher rather than a
 * human browser. An empty UA counts as a bot — real browsers always send one.
 */
export function isLikelyBot(ua: string | null | undefined): boolean {
  if (!ua || !ua.trim()) return true
  return BOT_UA_RE.test(ua)
}

/**
 * Detect a real mobile browser where it makes sense to pitch the native app.
 *
 * Returns `null` when:
 * - The UA is from our own Capacitor WebView (suffix appended in capacitor.config.ts).
 * - The UA is an Android in-app WebView (`; wv)`).
 * - The UA is one of the common in-app browsers (Facebook, Instagram, WeChat,
 *   Line, Twitter, LinkedIn). These embedded browsers frequently fail to open
 *   store URLs cleanly — pitching the app inside them produces broken handoffs.
 * - The UA is a bot / link unfurler (Slackbot, WhatsApp, Twitterbot, etc.).
 * - The UA is desktop or unrecognized mobile.
 */
export function detectMobileBrowser(ua: string | null | undefined): MobileBrowserPlatform | null {
  if (!ua) return null

  // Exclude Capacitor first — both iOS and Android Capacitor WebViews append
  // "Capacitor" to the UA via capacitor.config.ts.
  if (/Capacitor/i.test(ua)) return null

  // Exclude Android system WebView.
  if (/;\s*wv\)/.test(ua)) return null

  // Exclude in-app browsers — these embedded browsers can't reliably open
  // App Store / Play Store URLs.
  if (
    /\bFBA[NV]\b|\bFB_IAB\b|Instagram|MicroMessenger|Line\/|LinkedInApp|Twitter|YJApp|GSA/i.test(ua)
  ) {
    return null
  }

  // Exclude bots, link unfurlers, and email-security scanners.
  if (isLikelyBot(ua)) return null

  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios'
  if (/Android/i.test(ua)) return 'android'

  return null
}

/**
 * The single shareable install URL for every install CTA in the product.
 * Points at `/get-app`, which UA-sniffs server-side and 302s mobile UAs
 * straight to the correct store (or renders QR + badges for desktop).
 */
export function buildInstallLink(args: { path?: string; source?: string }): string {
  const params = new URLSearchParams()
  if (args.path && args.path.startsWith('/') && args.path !== '/') {
    params.set('path', args.path)
  }
  if (args.source) {
    params.set('s', args.source)
  }
  const qs = params.toString()
  return qs ? `/get-app?${qs}` : '/get-app'
}

/**
 * Plain store URL for the user's platform — used by `/get-app` to 302
 * directly to the store, and by the in-app-browser fallback (where the
 * embedded browser can usually open store URLs via OS intent).
 */
export function pickStoreUrlForUA(ua: string | null | undefined): string | null {
  const platform = detectMobileBrowser(ua)
  if (platform === 'ios') return IOS_APP_STORE_URL
  if (platform === 'android') return PLAY_STORE_URL
  return null
}

/** Store URL for an explicit platform (no UA sniffing). */
export function storeUrlForPlatform(platform: MobileBrowserPlatform): string {
  return platform === 'ios' ? IOS_APP_STORE_URL : PLAY_STORE_URL
}

/**
 * Build a path to the trackable `/get-app` redirect for a *specific* store
 * badge. Unlike `buildInstallLink` (UA-sniffs at the destination), this pins
 * the platform so an App Store badge always lands on the App Store and a Play
 * badge on Play — regardless of the device the email/landing page is opened
 * on. `/get-app` logs the click server-side (ad-block-proof) before the 302.
 *
 * Returns a relative path; callers serving outside the app origin (emails)
 * must prefix their absolute base URL.
 */
export function buildStoreRedirectPath(args: {
  platform: MobileBrowserPlatform
  source: string
}): string {
  const params = new URLSearchParams({ platform: args.platform, s: args.source })
  return `/get-app?${params.toString()}`
}

/**
 * Append UTM params to a store URL using the URL constructor so we don't break
 * the query string when the base already has one.
 *
 * `PLAY_STORE_URL` ends in `?id=net.alumnia.app` — naively appending
 * `?utm_source=...` produces a URL with two `?` characters, which Play Store
 * parses as `id = "net.alumnia.app?utm_source=..."` and 404s. The bug shipped
 * in every transactional email's Google Play badge and in the landing footer
 * before this helper landed.
 *
 * `IOS_APP_STORE_URL` happens to have no query string today, so the bug never
 * triggered on iOS — but routing both platforms through this helper keeps the
 * call sites symmetrical and protects against a future iOS URL change.
 */
export function appendStoreUtm(
  baseUrl: string,
  params: { source: string; medium: string; campaign: string }
): string {
  const u = new URL(baseUrl)
  u.searchParams.set('utm_source', params.source)
  u.searchParams.set('utm_medium', params.medium)
  u.searchParams.set('utm_campaign', params.campaign)
  return u.toString()
}
